import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// Colours at golden hour, in the blue hour halfway through, and at night.
const STOPS = {
  skyTop: ['#2E3A78', '#151B4A', '#050817'],
  skyMiddle: ['#8F4C8C', '#3A3176', '#0C1234'],
  skyLow: ['#EE765E', '#93508A', '#221D4C'],
  horizon: ['#FFC37E', '#E0827C', '#4C3366'],
  farTop: ['#A97494', '#4E4080', '#1A1C44'],
  farBottom: ['#D88E8C', '#6E4F86', '#262350'],
  midTop: ['#563A6C', '#29244F', '#0F122D'],
  midBottom: ['#7A4C6E', '#3A2E5E', '#16183A'],
  nearTop: ['#2C2140', '#181631', '#080A1C'],
  nearBottom: ['#3B2948', '#211D3D', '#0D0F26'],
  rim: ['#FFAA6C', '#C26C90', '#45488A'],
  haze: ['#F6A27E', '#9A5C8C', '#392E62'],
  waterTop: ['#B66D72', '#5C3A6E', '#1A1840'],
  waterBottom: ['#261C3E', '#121231', '#03050E'],
  bridge: ['#1C1427', '#110F26', '#05060F'],
  cloudLit: ['#FFB892', '#B76A8E', '#2B2B57'],
} as const;

type ColorName = keyof typeof STOPS;

export type CityColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light over the city at a time of evening (0 at golden hour, 1 at night). */
export const cityColors = (dusk: number): CityColors => {
  const scaled = clamp(dusk) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as CityColors,
  );
};

/** The sky over the header stays deep blue from sunset to night, so white header text throughout. */
export const cityChromeTone = (progress: number): ChromeTone => {
  const { skyTop, skyMiddle } = cityColors(progress);
  return luminance(skyTop) * 0.8 + luminance(skyMiddle) * 0.2 > 0.31 ? 'dark' : 'light';
};
